import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '@/routes/routeConstants';
import { tokenStorage } from '@/shared/utils/tokenStorage';

import type { IUser } from '../types/auth';

interface IAuthState {
  user: IUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export const useAuth = () => {
  const navigate = useNavigate();

  // Initialize state synchronously by checking tokenStorage immediately
  // This ensures token is available when opening new tabs (localStorage is shared across tabs)
  const initializeAuthState = (): IAuthState => {
    const token = tokenStorage.getAccessToken();
    if (!token) {
      return { user: null, isAuthenticated: false, isLoading: false };
    }

    // Restore user data from localStorage synchronously
    const userFromStorage = localStorage.getItem('user');
    let user: IUser | null = null;
    if (userFromStorage) {
      try {
        user = JSON.parse(userFromStorage);
      } catch {
        // Error parsing user from localStorage
      }
    }

    // If we have both token and user, user is authenticated
    return { user, isAuthenticated: !!user, isLoading: false };
  };

  const [state, setState] = useState<IAuthState>(initializeAuthState);

  const login = useCallback(
    async (accessToken: string, user: IUser, refreshToken?: string) => {
      // Store access token securely
      tokenStorage.setAccessToken(accessToken);
      // Store refresh token if provided
      if (refreshToken) {
        tokenStorage.setRefreshToken(refreshToken);
      }
      // Store user data in localStorage (less sensitive)
      localStorage.setItem('user', JSON.stringify(user));

      // Update state first to ensure isAuthenticated is true
      setState({ user, isAuthenticated: true, isLoading: false });

      // Navigate to dashboard immediately
      // Use replace to prevent going back to login page
      navigate(ROUTES.DASHBOARD, { replace: true });
    },
    [navigate]
  );

  const logout = useCallback(() => {
    // Clear all tokens
    tokenStorage.clearAllTokens();

    // Clear ALL localStorage to ensure complete logout
    // This ensures no data persists after logout
    try {
      localStorage.clear();
    } catch {
      // If clear() fails, manually remove known items
      localStorage.removeItem('user');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('currentprojectselected');
      localStorage.removeItem('chatbot-tooltip-shown');
      localStorage.removeItem('auth_error');
      localStorage.removeItem('theme');
    }

    setState({ user: null, isAuthenticated: false, isLoading: false });
    navigate('/login');
  }, [navigate]);

  const checkAuth = useCallback(async () => {
    // Load token from localStorage synchronously (tokenStorage loads in constructor)
    // This ensures token is available immediately when opening new tabs
    const token = tokenStorage.getAccessToken();
    if (!token) {
      setState({ user: null, isAuthenticated: false, isLoading: false });
      return;
    }

    try {
      // Here you would typically validate the token with your backend
      // For now, we'll just assume it's valid if it exists

      // Restore user data from localStorage
      const userFromStorage = localStorage.getItem('user');
      let user: IUser | null = null;

      if (userFromStorage) {
        try {
          user = JSON.parse(userFromStorage);
        } catch {
          // Error parsing user from localStorage
        }
      }

      // Require both token and user before treating session as authenticated
      setState({ user, isAuthenticated: Boolean(user), isLoading: false });
    } catch {
      // Auth check error
      logout();
    }
  }, [logout]);

  // Run checkAuth immediately on mount (before any components render)
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return {
    ...state,
    login,
    logout,
    checkAuth,
  };
};
